import React from 'react';
import { MUSCLES, LEGEND } from '../../data/muscleExplorerData';

const FRONT_SRC = '/anatomy/front.png?v=2';
const BACK_SRC = '/anatomy/back.png?v=2';

const HIT_TO_MUSCLE = {
  'f-chest': 'chest',
  'f-shoulders-l': 'shoulders',
  'f-shoulders-r': 'shoulders',
  'f-biceps-l': 'biceps',
  'f-biceps-r': 'biceps',
  'f-forearms-l': 'forearms',
  'f-forearms-r': 'forearms',
  'f-abs': 'abs',
  'f-quads-l': 'quads',
  'f-quads-r': 'quads',
  'f-calves-l': 'calves',
  'f-calves-r': 'calves',
  'b-shoulders-l': 'shoulders',
  'b-shoulders-r': 'shoulders',
  'b-triceps-l': 'triceps',
  'b-triceps-r': 'triceps',
  'b-back': 'back',
  'b-glutes': 'glutes',
  'b-hams-l': 'hamstrings',
  'b-hams-r': 'hamstrings',
  'b-calves-l': 'calves',
  'b-calves-r': 'calves',
};

function highlightedMuscles(selectedId) {
  const set = new Set();
  if (!selectedId) return set;
  const legend = LEGEND.find(l => l.id === selectedId);
  if (legend) legend.highlight.forEach(id => set.add(id));
  else set.add(selectedId);
  return set;
}

function muscleColor(id) {
  return MUSCLES[id]?.color || '#3ad4f6';
}

function muscleSides(id) {
  if (id === 'shoulders' || id === 'calves') return ['front', 'back'];
  return [MUSCLES[id]?.view || 'front'];
}

function Hit({ id, d, selectedId, hoveredId, onSelect, onHover }) {
  const muscle = HIT_TO_MUSCLE[id];
  const color = muscleColor(muscle);
  const activeSet = highlightedMuscles(selectedId);
  const isActive = activeSet.has(muscle);
  const isHovered = hoveredId === muscle;
  const label = MUSCLES[muscle]?.name || muscle;

  return (
    <path
      d={d}
      className={`me-hit${isActive ? ' is-active' : ''}${isHovered ? ' is-hovered' : ''}`}
      style={{ '--hit-color': color }}
      role="button"
      tabIndex={0}
      aria-label={label}
      onClick={(e) => { e.stopPropagation(); onSelect(muscle); }}
      onMouseEnter={() => onHover(muscle)}
      onMouseLeave={() => onHover(null)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(muscle);
        }
      }}
    />
  );
}

function FrontHits(props) {
  return (
    <svg viewBox="0 0 100 245" preserveAspectRatio="xMidYMid meet" aria-hidden="false">
      {/* Chest — stop above the abs */}
      <Hit id="f-chest" d="M37,50 C32,53 30,61 32,70 C34,76 40,78 50,78 C60,78 66,76 68,70 C70,61 68,53 63,50 C57,47 43,47 37,50 Z" {...props} />
      {/* Shoulders */}
      <Hit id="f-shoulders-l" d="M22,50 C16,52 14,60 18,68 C23,72 32,70 34,62 C35,54 30,48 22,50 Z" {...props} />
      <Hit id="f-shoulders-r" d="M78,50 C84,52 86,60 82,68 C77,72 68,70 66,62 C65,54 70,48 78,50 Z" {...props} />
      {/* Biceps */}
      <Hit id="f-biceps-l" d="M18,60 C12,63 11,78 13,90 C15,97 21,98 26,92 C29,86 29,72 26,64 C24,60 21,58 18,60 Z" {...props} />
      <Hit id="f-biceps-r" d="M82,60 C88,63 89,78 87,90 C85,97 79,98 74,92 C71,86 71,72 74,64 C76,60 79,58 82,60 Z" {...props} />
      {/* Forearms */}
      <Hit id="f-forearms-l" d="M10,92 C5,96 3,110 6,122 C9,130 16,128 20,118 C22,108 20,96 16,92 C14,90 12,90 10,92 Z" {...props} />
      <Hit id="f-forearms-r" d="M90,92 C95,96 97,110 94,122 C91,130 84,128 80,118 C78,108 80,96 84,92 C86,90 88,90 90,92 Z" {...props} />
      {/* Abs / core */}
      <Hit id="f-abs" d="M41,79 C39,84 38,100 39,114 C41,122 46,126 50,126 C54,126 59,122 61,114 C62,100 61,84 59,79 C55,76 45,76 41,79 Z" {...props} />
      {/* Quads */}
      <Hit id="f-quads-l" d="M30,122 C24,126 23,148 26,168 C28,180 36,184 44,176 C48,166 48,140 46,128 C44,122 36,118 30,122 Z" {...props} />
      <Hit id="f-quads-r" d="M70,122 C76,126 77,148 74,168 C72,180 64,184 56,176 C52,166 52,140 54,128 C56,122 64,118 70,122 Z" {...props} />
      {/* Calves */}
      <Hit id="f-calves-l" d="M32,176 C26,180 26,198 30,214 C33,222 40,222 44,212 C46,200 44,184 40,178 C38,174 34,174 32,176 Z" {...props} />
      <Hit id="f-calves-r" d="M68,176 C74,180 74,198 70,214 C67,222 60,222 56,212 C54,200 56,184 60,178 C62,174 66,174 68,176 Z" {...props} />
    </svg>
  );
}

function BackHits(props) {
  return (
    <svg viewBox="0 0 100 245" preserveAspectRatio="xMidYMid meet" aria-hidden="false">
      {/* Lats / mid-back */}
      <Hit id="b-back" d="M34,52 C26,58 22,78 26,102 C30,118 38,124 50,118 C62,124 70,118 74,102 C78,78 74,58 66,52 C58,48 42,48 34,52 Z" {...props} />
      {/* Rear delts */}
      <Hit id="b-shoulders-l" d="M22,50 C15,52 14,62 18,70 C24,74 32,70 34,62 C34,54 28,48 22,50 Z" {...props} />
      <Hit id="b-shoulders-r" d="M78,50 C85,52 86,62 82,70 C76,74 68,70 66,62 C66,54 72,48 78,50 Z" {...props} />
      {/* Triceps */}
      <Hit id="b-triceps-l" d="M16,62 C9,66 8,82 12,94 C16,102 24,100 28,90 C30,78 28,66 24,62 C22,60 18,60 16,62 Z" {...props} />
      <Hit id="b-triceps-r" d="M84,62 C91,66 92,82 88,94 C84,102 76,100 72,90 C70,78 72,66 76,62 C78,60 82,60 84,62 Z" {...props} />
      {/* Glutes */}
      <Hit id="b-glutes" d="M32,112 C26,118 26,136 32,146 C38,152 46,152 50,148 C54,152 62,152 68,146 C74,136 74,118 68,112 C60,106 40,106 32,112 Z" {...props} />
      {/* Hamstrings */}
      <Hit id="b-hams-l" d="M30,144 C24,148 24,168 28,186 C32,196 40,196 46,186 C48,172 46,152 42,146 C40,142 34,142 30,144 Z" {...props} />
      <Hit id="b-hams-r" d="M70,144 C76,148 76,168 72,186 C68,196 60,196 54,186 C52,172 54,152 58,146 C60,142 66,142 70,144 Z" {...props} />
      {/* Calves */}
      <Hit id="b-calves-l" d="M32,186 C26,190 26,208 30,222 C34,230 42,228 44,216 C46,204 42,190 38,186 C36,184 34,184 32,186 Z" {...props} />
      <Hit id="b-calves-r" d="M68,186 C74,190 74,208 70,222 C66,230 58,228 56,216 C54,204 58,190 62,186 C64,184 66,184 68,186 Z" {...props} />
    </svg>
  );
}

export function AnatomyFigure({ side, selectedId, hoveredId, onSelect, onHover, dimmed, focused }) {
  const src = side === 'back' ? BACK_SRC : FRONT_SRC;
  const Hits = side === 'back' ? BackHits : FrontHits;
  const label = hoveredId && muscleSides(hoveredId).includes(side)
    ? MUSCLES[hoveredId]?.name
    : null;

  return (
    <div className={`me-figure-wrap${dimmed ? ' is-dim' : ''}${focused ? ' is-focus' : ''}`}>
      <div className="me-figure">
        <img src={src} alt={`${side === 'back' ? 'Back' : 'Front'} muscle map`} draggable="false" />
        <Hits
          selectedId={selectedId}
          hoveredId={hoveredId}
          onSelect={onSelect}
          onHover={onHover}
        />
        {label && <div className="me-hover-label">{label}</div>}
      </div>
    </div>
  );
}

export function MusclePreview({ muscle }) {
  if (!muscle?.preview) return null;
  const src = muscle.preview.src === 'back' ? BACK_SRC : FRONT_SRC;
  return (
    <div className="me-preview" aria-hidden="true">
      <img
        src={src}
        alt=""
        style={{
          objectFit: 'cover',
          objectPosition: muscle.preview.pos,
          transformOrigin: muscle.preview.pos,
          transform: `scale(${muscle.preview.zoom})`,
        }}
      />
    </div>
  );
}

export { FRONT_SRC, BACK_SRC };
